import React, { Dispatch } from "react";
import { Callout, Intent } from "@blasterjs/core";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import ImageViewer from "../components/ImageViewer";
import InfoSidebarCase from "../components/InfoSidebarCase";
import TopBarCase from "../components/TopBarCase";
import Content from "../components/Content";
import { useAppDispatch, useAppSelector } from "../hooks";
import { caseFetch, resetCaseImageViewer } from "../slices/caseImageViewer";
import { studyFetchRequest } from "../slices/studyConfiguration";
import { queryDetailsFetchByCase, setCurrentViewQueryObjectType } from "../slices/queryDetails";
import { RolePermissions, userIsAdminOrReader } from "../permissions";
import { QueryObjectType } from "../models";
import { resetMetadataImages } from "../slices/metadataImages";
import { studyFetch } from "../slices/studies";
import ImageSelector from "../components/image-viewer/ImageSelector";

function resetCaseAll(dispatch: Dispatch<any>) {
  dispatch(resetCaseImageViewer());
  dispatch(resetMetadataImages());
}

const Case = () => {
  const params = useParams();
  const id = params.id || "no-id";
  const studyId = params.studyId || "no-study-id";

  const dispatch = useAppDispatch();

  const study = useAppSelector(state => state.studies.study);
  const histoCase = useAppSelector(state => state.caseImageViewer.histoCase);
  const selectedImage = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);
  const config = useAppSelector(state => state.config.config);
  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);
  const queryDetailsResource = useAppSelector(state => state.queryDetails);
  const queryDetails =
    "resource" in queryDetailsResource.queryDetails
      ? queryDetailsResource.queryDetails.resource
      : [];

  const studyName = ("resource" in study && study.resource.study.name) || null;

  useEffect(() => {
    dispatch(caseFetch(id));
    dispatch(studyFetchRequest(studyId));
    dispatch(studyFetch(studyId));
    dispatch(queryDetailsFetchByCase(id));
    dispatch(
      setCurrentViewQueryObjectType({
        queryViewObjectType: QueryObjectType.Case,
        viewObjectId: id
      })
    );
    return () => {
      resetCaseAll(dispatch);
    };
  }, [id, studyId]);

  const isUserAdminOrReader = userIsAdminOrReader(
    ("resource" in loggedInUser && loggedInUser.resource) || null
  );

  const hasAiAnnotations =
    (histoCase && "resource" in histoCase && histoCase.resource.acceptAiAnnotations) || false;

  const userCanDeleteAnnotations = isUserAdminOrReader;

  const errorText =
    "errorMessage" in config
      ? config.errorMessage
      : histoCase && "errorMessage" in histoCase
      ? histoCase.errorMessage
      : selectedImage && "errorMessage" in selectedImage
      ? selectedImage.errorMessage
      : null;

  const content =
    "resource" in config &&
    histoCase !== null &&
    "resource" in histoCase &&
    (selectedImage === null || "resource" in selectedImage) &&
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.AP_ImageViewer_ViewimageViewer]) ? (
      <ImageViewer
        imageWithAnnotations={selectedImage === null ? selectedImage : selectedImage.resource}
        tileServerLocation={config.resource.tileServerLocation}
        topBar={
          <TopBarCase
            histoCase={histoCase.resource.caseWithStatus}
            isCR={loggedInUser.resource.isCR()}
          />
        }
        sidebar={
          "resource" in loggedInUser &&
          (loggedInUser.resource.can([RolePermissions.AP_ImageCaseViewer_ViewOverviewtab]) ||
            loggedInUser.resource.isUploader()) ? (
            <InfoSidebarCase
              studyName={studyName}
              histoCase={histoCase.resource}
              selectedImage={selectedImage === null ? null : selectedImage.resource.imageAndQuery}
              userCanDeleteAnnotations={userCanDeleteAnnotations}
              queryDetails={queryDetails}
              loggedInUser={loggedInUser.resource}
              hasAiAnnotations={hasAiAnnotations}
            />
          ) : null
        }
        imageSelector={
          "resource" in loggedInUser &&
          (loggedInUser.resource.can([RolePermissions.AP_ImageCaseViewer_ViewOverviewtab]) ||
            loggedInUser.resource.isUploader()) ? (
            <ImageSelector histoCase={histoCase.resource} />
          ) : null
        }
      />
    ) : errorText ? (
      <Callout intent={Intent.DANGER}>{errorText}</Callout>
    ) : null;
  return (
    <Content
      isLoading={
        (histoCase !== null && "isPending" in histoCase) ||
        "isPending" in config ||
        (selectedImage !== null && "isPending" in selectedImage)
      }
    >
      {content}
    </Content>
  );
};

export default Case;
